import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useAPI } from 'global'
import { OpenBeforeAlarmDuration, OpenAlarmDuration } from 'enums'
import { Button, Checkbox, Select } from 'components'
import '../styles.scss'

export default function LockConfigsEdit(props) {
  const { t } = useTranslation(null, { useSuspense: false })
  const api = useAPI()
  const { lockConfigs, storeID, onFinish } = props
  const [useOrgDefaults, setUseOrgDefaults] = useState(!lockConfigs.storeDefault)
  const [allowMerchandize, setAllowMerchandize] = useState(!!lockConfigs.merchandize)
  const [allowStayUnlocked, setAllowStayUnlocked] = useState(!!lockConfigs.stayUnlocked)
  const useOrgCB = useRef()
  const allowMerchandizeCB = useRef()
  const allowStayUnlockedCB = useRef()
  const defaultBeforeDurSelect = useRef()
  const defaultDurSelect = useRef()
  const merchandizeBeforeDurSelect = useRef()
  const merchandizeDurSelect = useRef()
  const stayUnlockedBeforeDurSelect = useRef()
  const stayUnlockedDurSelect = useRef()
  const defaultConf = lockConfigs.storeDefault?.configurationSettings || lockConfigs.orgDefault?.defaultConfiguration
  let merchandizeBeforeDur = lockConfigs.merchandize?.configurationSettings?.durationBeforeAlarm
  let merchandizeAlarmDur = lockConfigs.merchandize?.configurationSettings?.alarmDuration
  if (merchandizeBeforeDur == null) merchandizeBeforeDur = defaultConf.durationBeforeAlarm
  if (merchandizeAlarmDur == null) merchandizeAlarmDur = defaultConf.alarmDuration
  let stayUnlockedBeforeDur = lockConfigs.stayUnlocked?.configurationSettings?.durationBeforeAlarm
  let stayUnlockedAlarmDur = lockConfigs.stayUnlocked?.configurationSettings?.alarmDuration
  if (stayUnlockedBeforeDur == null) stayUnlockedBeforeDur = defaultConf.durationBeforeAlarm
  if (stayUnlockedAlarmDur == null) stayUnlockedAlarmDur = defaultConf.alarmDuration

  const saveConfig = async (name, current, clear, beforeSelect, alarmSelect, stayUnlocked = false) => {
    if (clear) {
      if (current) api.destroyStoreConfig(current.id).then(() => {
        toast.success(t('lockConfigEdit.configDestroyed', {name: current.name}))
      }).catch(r => {
        const e = t('lockConfigEdit.destroyFailed', { name: current.name })
        console.error(e, r)
        toast.error(e)
      })
      return
    }

    const newBeforeDur = Number(beforeSelect.current?.value || 0)
    const newAlarmDur = Number(alarmSelect.current?.value || 0)
    const cur = current?.configurationSettings
    if (cur?.durationBeforeAlarm == newBeforeDur && cur?.alarmDuration == newAlarmDur)
      return current // Nothing's changed here, so just return

    const newConfig = {
      ...current,
      nodeConfigurationTypeId: lockConfigs.orgDefault.id,
      storeId: storeID,
      name: name,
      securityDevicesOnly: stayUnlocked,
      configurationSettings: {
        durationBeforeAlarm: newBeforeDur,
        alarmDuration: newAlarmDur,
        stayUnlocked: stayUnlocked,
      },
    }

    return await api.saveStoreConfig(newConfig).then(c => {
      toast.success(t('lockConfigEdit.configSaved', {name: c.name}))
      return c
    }).catch(r => {
      const e = t('lockConfigEdit.saveFailed', { name: newConfig.name })
      console.error(e, r)
      toast.error(e)
    })
  }

  const onSave = async () => {
    const newLockConfigs = { ...lockConfigs }
    newLockConfigs.storeDefault = await saveConfig('Default', lockConfigs.storeDefault, useOrgDefaults, defaultBeforeDurSelect, defaultDurSelect)
    newLockConfigs.merchandize = await saveConfig('Merchandize', lockConfigs.merchandize, !allowMerchandize, merchandizeBeforeDurSelect, merchandizeDurSelect)
    newLockConfigs.stayUnlocked = await saveConfig('Stay Unlocked', lockConfigs.stayUnlocked, !allowStayUnlocked, stayUnlockedBeforeDurSelect, stayUnlockedDurSelect, allowStayUnlockedCB.current.checked)
    if (onFinish) onFinish(newLockConfigs)
  }

  const beforeDurOptions = Object.keys(OpenBeforeAlarmDuration).map(k => {
    return <option key={k} value={OpenBeforeAlarmDuration[k]}>{t(`deviceConfig.${k}`)}</option>
  })
  const alarmDurOptions = Object.keys(OpenAlarmDuration).map(k => {
    return <option key={k} value={OpenAlarmDuration[k]}>{t(`deviceConfig.${k}`)}</option>
  })

  return (
    <div className="store-lock-configs-edit-comp col">
      <Checkbox ref={useOrgCB} label={t('lockConfigEdit.useOrgDefault')} defaultChecked={useOrgDefaults} onChange={e=>setUseOrgDefaults(e.target.checked)} />
      {!useOrgDefaults && (
        <>
          <Select label={t('deviceConfig.openBeforeAlarmDur')} ref={defaultBeforeDurSelect} defaultValue={defaultConf?.durationBeforeAlarm}>{beforeDurOptions}</Select>
          <Select label={t('deviceConfig.openAlarmDur')} ref={defaultDurSelect} defaultValue={defaultConf?.alarmDuration}>{alarmDurOptions}</Select>
        </>
      )}

      <Checkbox ref={allowMerchandizeCB} label={t('lockConfigEdit.allowMerchandize')} defaultChecked={!!lockConfigs.merchandize} onChange={e=>setAllowMerchandize(e.target.checked)} />
      {allowMerchandize && (
        <>
          <Select label={t('deviceConfig.openBeforeAlarmDur')} ref={merchandizeBeforeDurSelect} defaultValue={merchandizeBeforeDur}>{beforeDurOptions}</Select>
          <Select label={t('deviceConfig.openAlarmDur')} ref={merchandizeDurSelect} defaultValue={merchandizeAlarmDur}>{alarmDurOptions}</Select>
        </>
      )}

      <Checkbox ref={allowStayUnlockedCB} label={t('lockConfigEdit.allowStayUnlocked')} defaultChecked={!!lockConfigs.stayUnlocked} onChange={e=>setAllowStayUnlocked(e.target.checked)} />
      {allowStayUnlocked &&
        <>
          <Select label={t('deviceConfig.openBeforeAlarmDur')} ref={stayUnlockedBeforeDurSelect} defaultValue={stayUnlockedBeforeDur}>{beforeDurOptions}</Select>
          <Select label={t('deviceConfig.openAlarmDur')} ref={stayUnlockedDurSelect} defaultValue={stayUnlockedAlarmDur}>{alarmDurOptions}</Select>
        </>
      }

      <Button onClick={onSave}>{t('form.save')}</Button>

    </div>
  )
}
